import { Route } from 'react-router-dom';
import { Navigate } from '~/utils/navigation';
import { TracedRoutes } from '@comeon/error-reporter';
import { ExplorePageContainer, PoolXContainer } from './sportsbook-async';
import {
	ROUTE_SPORTSBOOK,
	ROUTE_SPORTSBOOK_SPORTS_PAGE,
	ROUTE_SPORTSBOOK_EVENTS,
	ROUTE_SPORTSBOOK_MY_BETS,
	ROUTE_SPORTSBOOK_LEAGUE_PAGE,
	ROUTE_SPORTSBOOK_BROWSE,
	ROUTE_SPORTSBOOK_LIVE_PAGE,
	ROUTE_SPORTSBOOK_FLAT_LEAGUE_PAGE,
	ROUTE_SPORTSBOOK_EXPLORE_PAGE,
	ROUTE_SPORTSBOOK_EVENT_PAGE,
	ROUTE_SPORTSBOOK_FLAT_EVENT,
	ROUTE_SPORTSBOOK_SEARCH_PAGE
} from '../../router/constants';
import { getRelativePaths } from '../../../utils/pathname';
import useContextualPathTransformer from '../../../hooks/use-contextual-path-transformer';
import LeaguePageSubRoutes from './features/league-page/sportsbook-league-page-sub-routes';
import LivePageSubRoutes from './features/live-page/sportsbook-live-page-sub-routes';
import { TAB_OPEN_BETS_ROUTE, TAB_SETTLED_BETS_ROUTE } from './sportsbook-constants';
import {
	SportsbookSportPageContainer,
	SportsbookSportOutrightsContainer,
	SportsbookSportAllLeaguesContainer,
	SportsbookSportEventsContainer
} from './features/sport-page/sportsbook-sport-async';
import {
	SportsbookBrowsePageContainer,
	SportsbookBrowseSportsContainer,
	SportsbookBrowseLeaguesContainer
} from './features/browse-page/sportsbook-browse-async';
import { SportsbookLivePageContainer, SportsbookLiveSportsContainer } from './features/live-page/sportsbook-live-async';
import {
	SportsbookMyBetsPageContainer,
	SportsbookMyBetsOpenContainer,
	SportsbookMyBetsSettledContainer
} from './features/my-bets-page/sportsbook-my-bets-async';
import {
	SportsbookLeaguePageContainer,
	SportsbookLeagueEventsContainer,
	SportsbookLeagueOutrightsContainer,
	SportsbookLeagueTournamentPreviewContainer
} from './features/league-page/sportsbook-league-async';
import { SearchPageContainer } from '~/components/sportsbook/lobby/features/search/sportsbook-search-async';
import SportsPageSubRoutes from './features/sport-page/sportsbook-sport-page-sub-routes';
import {
	SportsbookEventBetBuilderContainer,
	SportsbookEventMarketsContainer,
	SportsbookEventOpenBetsContainer,
	SportsbookEventPageContainer
} from './features/event-page/sportsbook-event-async';
import EventPageSubRoutes from './features/event-page/sportsbook-event-page-sub-routes';
import { useSelector } from 'react-redux';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { selectGlobalNavigationEnabled } from '~/components/brand/ducks/selectors';
import { useMemo } from 'react';

const p = {
	browse: getRelativePaths(ROUTE_SPORTSBOOK_BROWSE, ROUTE_SPORTSBOOK),
	search: getRelativePaths(ROUTE_SPORTSBOOK_SEARCH_PAGE, ROUTE_SPORTSBOOK),
	live: getRelativePaths(ROUTE_SPORTSBOOK_LIVE_PAGE, ROUTE_SPORTSBOOK),
	sports: getRelativePaths(ROUTE_SPORTSBOOK_SPORTS_PAGE, ROUTE_SPORTSBOOK),
	myBets: getRelativePaths(ROUTE_SPORTSBOOK_MY_BETS, ROUTE_SPORTSBOOK),
	event: getRelativePaths(ROUTE_SPORTSBOOK_EVENT_PAGE, ROUTE_SPORTSBOOK),
	league: getRelativePaths(ROUTE_SPORTSBOOK_LEAGUE_PAGE, ROUTE_SPORTSBOOK),
	flatLeague: getRelativePaths(ROUTE_SPORTSBOOK_FLAT_LEAGUE_PAGE, ROUTE_SPORTSBOOK),
	flatEvent: getRelativePaths(ROUTE_SPORTSBOOK_FLAT_EVENT, ROUTE_SPORTSBOOK)
};

/**
 * Component that contains all the root routes for the sportsbook
 */
const SportsbookRoutes: React.FC = () => {
	const transformPath = useContextualPathTransformer();
	const isSmaller = useSelector(isSmallerSelector);
	const isGlobalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);

	const shouldDisplaySearchPage = useMemo(() => {
		return isSmaller && isGlobalNavigationEnabled;
	}, [isGlobalNavigationEnabled, isSmaller]);

	return (
		<TracedRoutes>
			<Route index element={<ExplorePageContainer />} />
			{shouldDisplaySearchPage && <Route path={`${p.search}/*`} element={<SearchPageContainer />} />}
			<Route path={`${p.browse}/*`} element={<SportsbookBrowsePageContainer />}>
				<Route index element={<SportsbookBrowseSportsContainer />} />
				<Route path="leagues" element={<SportsbookBrowseLeaguesContainer />} />
			</Route>
			<Route path={`${p.live}/*`} element={<SportsbookLivePageContainer />}>
				<Route index element={<SportsbookLiveSportsContainer />} />
				{LivePageSubRoutes.SPORT.map(path => (
					<Route key={path} path={path} element={<SportsbookLiveSportsContainer />} />
				))}
			</Route>
			<Route path={`${p.sports}/:sportIdWithSlug/*`} element={<SportsbookSportPageContainer />}>
				<Route index element={<SportsbookSportEventsContainer />} />
				<Route path={SportsPageSubRoutes.LEAGUES} element={<SportsbookSportAllLeaguesContainer />} />
				<Route path={SportsPageSubRoutes.OUTRIGHTS} element={<SportsbookSportOutrightsContainer />} />

				{/* Redirect from old routes happen in games container */}
				{SportsPageSubRoutes.UPCOMING.map(subRoute => (
					<Route key={subRoute} path={subRoute} element={<SportsbookSportEventsContainer />} />
				))}
				<Route path={SportsPageSubRoutes.LIVE} element={<SportsbookSportEventsContainer />} />
			</Route>
			<Route path={`${p.myBets}/*`} element={<SportsbookMyBetsPageContainer />}>
				<Route path={TAB_SETTLED_BETS_ROUTE} element={<SportsbookMyBetsSettledContainer />} />
				<Route path={TAB_OPEN_BETS_ROUTE} element={<SportsbookMyBetsOpenContainer />} />
				<Route
					path=""
					element={<Navigate to={transformPath(TAB_OPEN_BETS_ROUTE, { preserveContexts: true })} replace />}
				/>
			</Route>

			<Route path={`${p.event}/*`} element={<SportsbookEventPageContainer />}>
				<Route index element={<SportsbookEventMarketsContainer />} />
				<Route path={EventPageSubRoutes.BET_BUILDER} element={<SportsbookEventBetBuilderContainer />} />
				<Route path={EventPageSubRoutes.OPEN_BETS} element={<SportsbookEventOpenBetsContainer />} />
			</Route>

			<Route path={`${p.flatEvent}/*`} element={<SportsbookEventPageContainer />} />

			<Route path={`${p.league}/*`} element={<SportsbookLeaguePageContainer />}>
				<Route index element={<SportsbookLeagueEventsContainer />} />
				<Route path={LeaguePageSubRoutes.OUTRIGHTS} element={<SportsbookLeagueOutrightsContainer />} />
				<Route
					path={LeaguePageSubRoutes.TOURNAMENT_PREVIEW}
					element={<SportsbookLeagueTournamentPreviewContainer />}
				/>
			</Route>
			<Route path={`${p.flatLeague}/*`} element={<SportsbookLeaguePageContainer />} />
			<Route path="poolx/*" element={<PoolXContainer />} />
			{/* Redirects */}
			<Route
				path={ROUTE_SPORTSBOOK_EVENTS}
				element={<Navigate to={transformPath(ROUTE_SPORTSBOOK_EXPLORE_PAGE, { preserveContexts: true })} />}
			/>
		</TracedRoutes>
	);
};

export default SportsbookRoutes;
